/**
 * @file MyClients.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 * @prop {Array} customers - List of customers
 * @prop {Function} fetchClients - Function to fetch clients
 * @prop {Boolean} addClients - Show add clients button
 * @prop {String} allocation - Allocation type (strategies/advisor)
 */

import React, { useCallback, useEffect, useState } from "react";
import Widget from "../Widget";
import { LineChart, Line, ReferenceLine, YAxis } from "recharts";
import { useForm } from "../../../../utils/useForm";
import Swal from "sweetalert2";
import SelectInput from "../../../../components/forms/SelectInput";
import TextInput from "../../../../components/forms/TextInput";
import DgModal from "../../../../components/DgModal";
import Request from "../../../../utils/fetchUtils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TextSnippet from "@mui/icons-material/TextSnippet";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { toISOString } from "../../../../utils/dateUtils";
import { IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router";
import { mapHistoricalToLineChart } from "../../platformUtils";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import DgButton from "../../../../components/DgButton";

const noop = () => {};

// Distinguishes "not linked" / "needs authorization" / "sync failed" instead
// of collapsing every unlinked state into the old generic `warning` string
// (ADFE-150) — falls back to that string for customers a stage that hasn't
// deployed the new brokerLinkStatus field yet.
const getLinkStatusBadge = (customer) => {
  switch (customer.brokerLinkStatus) {
    case "not_linked":
      return { label: "not linked", color: "#9e9e9e", tooltip: "Tradier application not yet approved" };
    case "needs_authorization":
      return { label: "needs authorization", color: "#F5A623", tooltip: "Approved — awaiting customer authorization" };
    case "sync_failed":
      return {
        label: "sync failed",
        color: "#e74c3c",
        tooltip: customer.lastSyncError || "Failed to sync broker account",
      };
    default:
      return customer.warning ? { label: "not linked", color: "#F5A623", tooltip: customer.warning } : null;
  }
};

function MyClients({ customers, fetchClients = noop, addClients, allocation }) {
  var user = JSON.parse(localStorage.session);
  const role = user.role;
  const navigate = useNavigate();
  const [openModalAllocation, setOpenModalAllocation] = useState(false);
  const [openModalNotes, setOpenModalNotes] = useState(false);
  const [openModalEditRisk, setOpenModalEditRisk] = useState(false);
  const [note, setNote] = useState("");
  const [strategies, setStrategies] = useState([]);
  const [advisors, setAdvisors] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [riskScoreInput, setRiskScoreInput] = useState("");
  const [riskScoreLoading, setRiskScoreLoading] = useState(false);
  
  const [
    customerAllocationData,
    setCustomerAllocationData,
    resetAllocationData,
  ] = useForm({
    percentage: "",
    strategyId: "",
    accountId: "",
  });
  const [selectedAdvisor, setSelectedAdvisor] = useForm({
    advisorKey: "",
  });

  const [customerAllocationValidation, setCustomerAllocationValidation] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [percentageWarning, setPercentageWarning] = useState(false);
  const [fees, setFees] = useState([]);
  let data = [];

  const handleFetchFeesData = async () => {
    const sess = JSON.parse(localStorage.session);
    const userId = sess?.userId;
    if (typeof userId === "string") {
      setLoading(true);
      const res = await Request(`/trading/fees`);
      if (res.ok) {
        setLoading(false);
        data = res.data;
        setFees(data);
      }
    }
  };

  const getClientFees = (clientSK, feesArray) => {
    const clientFees = feesArray.find(fee => fee.clientId === clientSK);

    if (clientFees) {
      return {
        monthlyFee: clientFees.monthlyFee || 0,
        feePercentage: clientFees.feePercentage || 0,
        advisorFeePercentage: clientFees.advisorFeePercentage || 0
      };
    }
    return {
      monthlyFee: 0,
      feePercentage: 0,
      advisorFeePercentage: 0
    };
  };

  // Check if user can edit risk scores
  const canEditRiskScore = role === 'admin' || role === 'fundManager' || role === 'advisor';

  // Function to calculate risk difference and return appropriate indicator
  const getRiskDifferenceIndicator = (riskAssessment, weightedRisk) => {
    // Handle null, undefined, or empty values
    const riskScore = parseFloat(riskAssessment) || 0;
    const weightedScore = parseFloat(weightedRisk);
    
    // If weighted risk is null, undefined, or empty, don't show indicator
    if (weightedScore === null || weightedScore === undefined || weightedRisk === '' || isNaN(weightedScore)) {
      return null;
    }
    
    const difference = Math.abs(riskScore - weightedScore);
    
    if (difference < 15) {
      return {
        icon: <CheckCircleIcon style={{ fontSize: "18px", color: "#4CAF50" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (Good match)`,
        color: "#4CAF50"
      };
    } else if (difference >= 15 && difference <= 30) {
      return {
        icon: <WarningIcon style={{ fontSize: "18px", color: "#FF9800" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (Moderate difference)`,
        color: "#FF9800"
      };
    } else {
      return {
        icon: <ErrorIcon style={{ fontSize: "18px", color: "#F44336" }} />,
        tooltip: `Risk difference: ${difference.toFixed(1)} (High difference)`,
        color: "#F44336"
      };
    }
  };

  const fetchFees = useCallback(() => {
    handleFetchFeesData();
  }, []);

  const handleCustomerAllocation = async () => {
    const client = selectedCustomer;
    const { strategyId } = customerAllocationData;
    const res = await Request(
      `/trading/strategy/${strategyId}/add-client`,
      {
        customerKey: client.SK,
        ...customerAllocationData,
      },
      "PATCH"
    );
    setOpenModalAllocation(false);
    if (res.ok) {
      Swal.fire({
        title: "Customer allocated",
        text: "",
        icon: "success",
      }).then(() => {
        resetAllocationData();
      });
    } else {
      Swal.fire({
        title: "Oops",
        text: res.message,
        icon: "error",
      });
    }
  };

  const handleAddNote = async () => {
    if (note.length > 0) {
      const { notes = [], SK } = selectedCustomer;
      const newNote = {
        content: note,
        createdBy: localStorage.getItem("SK"),
        createdAt: toISOString(),
      };
      
      // Create updated notes array
      const updatedNotes = [...notes, newNote];
      
      const res = await Request(
        "/user",
        {
          notes: updatedNotes,
        },
        "PUT",
        { Userkey: SK }
      );
      
      if (res.ok) {
        Swal.fire("Note added", "", "success");
        setNote("");
        
        // Update the selectedCustomer state immediately to reflect the new note
        setSelectedCustomer(prevCustomer => ({
          ...prevCustomer,
          notes: updatedNotes
        }));
        
        // Still call fetchClients to ensure data consistency across the app
        fetchClients();
      }
    } else {
      Swal.fire(
        "Empty note!",
        "Please add something in note section",
        "warning"
      );
    }
  };

  // Risk Score editing functions
  const handleEditRiskScore = (customer) => {
    setSelectedCustomer(customer);
    setRiskScoreInput(customer.score || "");
    setOpenModalEditRisk(true);
  };

  const updateCustomerRiskScore = async () => {
    const score = parseFloat(riskScoreInput);
    
    // Validation
    if (!riskScoreInput || isNaN(score) || score < 0) {
      Swal.fire(
        "Invalid Input!",
        "Please enter a valid risk score greater than or equal to 0",
        "warning"
      );
      return;
    }

    setRiskScoreLoading(true);
    try {
      const res = await Request(
        '/user/score', 
        {
          userId: selectedCustomer.userId,
          score: score,
          SK: selectedCustomer.SK
        },
        'PUT'
      );

      setRiskScoreLoading(false);
      
      if (res.ok) {
        setOpenModalEditRisk(false);
        
        Swal.fire('Updated!', 'Customer risk score has been updated successfully', 'success');
        
        // Refresh clients list to show updated score
        fetchClients();
      } else {
        Swal.fire('Error', 'Could not update customer risk score', 'error');
      }
    } catch (error) {
      setRiskScoreLoading(false);
      Swal.fire('Error', 'An error occurred while updating customer risk score', 'error');
    }
  };

  const fetchAdvisors = async () => {
    const res = await Request("/user?role=advisor");
    if (res.ok) {
      const arrayAdvisors = [];
      res.data.forEach((advisor, index) => {
        arrayAdvisors.push({
          advisorKey: advisor.SK,
          name: advisor.fullName,
        });
      });
      setAdvisors(arrayAdvisors);
    }
  };

  const fetchData = useCallback(async () => {
    if (allocation === "strategies") {
      setLoading(true);
      const res = await Request(`/trading/strategy`);
      setLoading(false);
      if (res.ok) {
        setStrategies(res.data);
      }
    } else if (allocation === "advisor") {
      setLoading(true);
      await fetchAdvisors();
      setLoading(false);
    }
  }, []);

  const handleViewClientDetails = (customer) => {
    const userRoute = `/dashboard/customer/${customer.SK}`;
    navigate(userRoute, { state: customer });
  };

  useEffect(() => {
    fetchData();
    fetchFees();
  }, []);

  useEffect(() => {
    const { percentage, strategyId, accountId } = customerAllocationData;

    if (!percentage || !strategyId || !accountId) {
      setCustomerAllocationValidation(true);
    } else {
      setCustomerAllocationValidation(false);
    }

    if (percentage && percentage > 100 - selectedCustomer?.usedPercentage) {
      setPercentageWarning(true);
      setCustomerAllocationValidation(true);
    } else {
      setPercentageWarning(false);
    }
  }, [customerAllocationData]);

  return (
    <Widget name={"my-clients"} width={"full-width"}>
      <div className="toppers">
        {addClients && (
          <div className="new-client">
            <img className="icon" src="/assets/icons/plus.svg" alt="" />
            <p className="new-client-button">New client</p>
          </div>
        )}
        <FilterSelect onFilter={fetchClients} />
      </div>
      <table className="clients-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Risk Assesment</th>
            <th>Weighted Risk</th>
            <th>Risk Match</th>
            <th>Performance</th>
            <th># Strategies</th>
            <th>Percentage Allocated</th>
            <th>Amount</th>
            <th>Fees</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers ? (
            customers.map((customer) => {
              const mappedHistorical = mapHistoricalToLineChart(
                customer.historical
              );
              const riskIndicator = getRiskDifferenceIndicator(customer.score, customer.weightedRiskScore);
              return (
                <tr key={customer.PK}>                  
                  <td className="name">
                    <div className="user">
                      <div className="profile-pic" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: '#95B2C5',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px'
                      }}>
                        <PersonIcon sx={{ fontSize: 24, color: '#021C2D' }} />
                      </div>
                      <div>
                        <div className="activity-box-account">
                          <span className="name">{customer.fullName}</span>
                          <span className="activity">
                            Account Number: {customer.broker?.body?.balances?.account_number || 'N/A'}
                            {(() => {
                              const linkStatus = getLinkStatusBadge(customer);
                              return linkStatus && (
                                <Tooltip title={linkStatus.tooltip} placement="top">
                                  <span style={{ color: linkStatus.color, marginLeft: "4px" }}>
                                    ({linkStatus.label})
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </span>
                          <span className="activity">
                            Last activity: {customer.updatedAt}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="score" style={{ position: 'relative' }}>
                    <div style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "8px" 
                    }}>
                      <p className="score-text">({customer.score || 0})</p>
                      {canEditRiskScore && (
                        <Tooltip title="Edit Risk Score" placement="top">
                          <EditIcon 
                            style={{ 
                              fontSize: "16px", 
                              cursor: "pointer", 
                              color: "#3357FF",
                              opacity: 0.7
                            }}
                            onClick={() => handleEditRiskScore(customer)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="weighted-risk">
                    <span>
                      {customer.weightedRiskScore != null && customer.weightedRiskScore !== ''
                        ? customer.weightedRiskScore
                        : '-'}
                    </span>
                  </td>
                  <td className="risk-match" style={{ textAlign: 'center' }}>
                    {riskIndicator ? (
                      <Tooltip title={riskIndicator.tooltip} placement="top">
                        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                          {riskIndicator.icon}
                        </div>
                      </Tooltip>
                    ) : (
                      <span style={{ color: '#666' }}>-</span>
                    )}
                  </td>
                  <td className="performance">
                    {customer.historical && (
                      <div className="performance-detail">
                        <LineChart
                          width={200}
                          height={100}
                          data={mappedHistorical.data}
                        >
                          <Line
                            type="monotone"
                            dataKey="Performance"
                            stroke="#ABECD6"
                            strokeWidth={3}
                          />
                          <Tooltip />
                          <ReferenceLine
                            y={0}
                            stroke="white"
                            strokeDasharray="3 3"
                          />
                          <YAxis
                            axisLine={false}
                            tick={false}
                            stroke="#95B2C5"
                            domain={[
                              mappedHistorical.minValue,
                              mappedHistorical.maxValue,
                            ]}
                          />
                        </LineChart>
                      </div>
                    )}
                  </td>
                  <td className="strategy">
                    <span className="strategy-title">
                      {customer.strategies?.length || 0}
                    </span>
                  </td>
                  <td className="score">
                    <p className="score-text">{customer.usedPercentage} % </p>
                  </td>
                  <td className="account">
                    {(() => {
                      const linkStatus = getLinkStatusBadge(customer);
                      const totalEquity = customer.broker?.body?.balances?.total_equity;

                      if (linkStatus) {
                        return (
                          <Tooltip title={linkStatus.tooltip} placement="top">
                            <span className="amount" style={{ fontSize: "12px", color: linkStatus.color }}>
                              N/A
                            </span>
                          </Tooltip>
                        );
                      }

                      // Linked but the balance itself is missing: a fetch
                      // failure, not a real $0 (ADFE-150) — don't conflate them.
                      if (typeof totalEquity !== "number") {
                        return (
                          <span className="amount" style={{ fontSize: "12px", color: "#9e9e9e" }}>
                            —
                          </span>
                        );
                      }

                      return (
                        <span className="amount" style={{ fontSize: "12px" }}>
                          ${totalEquity.toLocaleString("en-US")}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="fees">
                    <div className="fees-detail">
                      {(() => {
                        const clientFees = getClientFees(customer.SK, fees);
                        return (
                          <p>
                            Advisor fees $: <span>${clientFees.monthlyFee.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <br />
                            Advisor fees %: <span>{(clientFees.advisorFeePercentage * 100).toFixed(2)}%</span>
                          </p>
                        );
                      })()}
                    </div>
                  </td>
                  <td>
                    <Tooltip title="Notes" placement="top-start">
                      <IconButton
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setOpenModalNotes(true);
                        }}
                      >
                        <TextSnippet sx={{ color: "white" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Assign strategy" placement="top-start">
                      <IconButton
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setOpenModalAllocation(true);
                        }}
                      >
                        <AssignmentIndIcon sx={{ color: "white" }} />
                      </IconButton>
                    </Tooltip>
                    {customer && customer?.brokerAccounts ? (
                      <Tooltip title="View Details" placement="top-start">
                        <IconButton
                          onClick={() => handleViewClientDetails(customer)}
                        >
                          <VisibilityIcon sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              {" "}
              <td colSpan={9}>No customers assigned</td>{" "}
            </tr>
          )}
        </tbody>
      </table>

      {/* Edit Risk Score Modal */}
      {canEditRiskScore && (
        <DgModal
          modalState={openModalEditRisk}
          handleModalState={() => setOpenModalEditRisk(false)}
        >
          <h1 className="title">Edit Customer Risk Score</h1>
          <div className="customer-info" style={{ marginBottom: "20px", color: "#95b2c5" }}>
            <h3>{selectedCustomer.fullName}</h3>
            <p>Current Risk Score: {selectedCustomer.score || 0}</p>
          </div>
          <div className="strategy-content">
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Risk Score",
                  name: "riskScore",
                  type: "number",
                  tooltipBool: true,
                  tooltipText: "Enter a risk score value (minimum 0)",
                  value: riskScoreInput,
                  action: (e) => {
                    setRiskScoreInput(e.target.value);
                  },
                  min: 0,
                  step: 0.1
                }}
              />
            </div>
          </div>
          <DgButton
            category="primary"
            size="large"
            text="Update Risk Score"
            disabled={riskScoreLoading}
            action={updateCustomerRiskScore}
          />
        </DgModal>
      )}

      {/* Allocation Modal */}
      {openModalAllocation && (
        <div className={"dgmodal component open flex to-allocate-clients"}>
          <div className="modal">
            <div
              className="close"
              onClick={() => {
                setOpenModalAllocation(false);
              }}
            >
              x
            </div>
            <div>
              <div className="heading">
                <h2 className="subtitle">Allocating customer</h2>
              </div>
              <div className="customer">
                {allocation === "strategies" ? (
                  <>
                    <p>
                      Name: {selectedCustomer.fullName}
                      <br />
                      Risk score: {selectedCustomer.score}
                      <br />
                      Amount invested: ${selectedCustomer.score}
                      <br />
                      Used percentage: {selectedCustomer.usedPercentage}%
                      <br />
                    </p>
                    <p>
                      Remaining percentage:{" "}
                      {100 - selectedCustomer.usedPercentage}%<br />
                      Remaining amount to invest: $
                      {((100 - selectedCustomer.usedPercentage) / 100) *
                        selectedCustomer.score}
                      <br />
                    </p>
                  </>
                ) : (
                  <>
                    <h3>{selectedCustomer.fullName}</h3>
                  </>
                )}
              </div>
              {allocation === "strategies" ? (
                <>
                  <div className="form-field">
                    <SelectInput
                      customInput={{
                        label: "Strategy",
                        name: "strategyId",
                        tooltipBool: false,
                        value: customerAllocationData.strategyId,
                        action: (e) => {
                          setCustomerAllocationData(e);
                        },
                      }}
                      data={[
                        { value: "", text: "" },
                        ...strategies.map((s) => ({
                          value: s.strategyId,
                          text: s.name,
                          disabled: !s.feePercentage,
                        })),
                      ]}
                    />
                  </div>
                  <div className="form-field">
                    <TextInput
                      customInput={{
                        type: "number",
                        label: "Percentage",
                        name: "percentage",
                        tooltipBool: false,
                        value: customerAllocationData.percentage,
                        action: (e) => {
                          setCustomerAllocationData(e);
                        },
                      }}
                    />
                  </div>
                  <div className="form-field">
                    <SelectInput
                      customInput={{
                        label: "Account ID",
                        name: "accountId",
                        tooltipBool: false,
                        value: customerAllocationData.accountId,
                        action: (e) => {
                          setCustomerAllocationData(e);
                        },
                      }}
                      data={[
                        { value: "", text: "" },
                        ...(selectedCustomer?.brokerAccounts?.map(
                          ({ id, environment, provider }) => ({
                            value: id,
                            text: `${environment}: ${provider} - ${id}`,
                          })
                        ) || []),
                      ]}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-field">
                    <SelectInput
                      customInput={{
                        label: "Select an advisor",
                        name: "advisorKey",
                        tooltipBool: false,
                        value: selectedAdvisor.advisorKey,
                        action: (e) => {
                          setSelectedAdvisor(e);
                        },
                      }}
                      data={[
                        { value: "", text: "" },
                        ...advisors.map((a) => ({
                          value: a.advisorKey,
                          text: a.name,
                        })),
                      ]}
                    />
                  </div>
                </>
              )}
            </div>
            {percentageWarning && (
              <p className="warning">
                The inserted percentage should not be bigger than the available
                percentage.
              </p>
            )}
            {/* TODO: fix difference between disabled and loading */}
            <button
              className="primary large"
              disabled={customerAllocationValidation}
              onClick={() => {
                handleCustomerAllocation();
              }}
            >
              Assign strategy
            </button>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      <DgModal
        modalState={openModalNotes}
        handleModalState={() => {
          setOpenModalNotes(false);
        }}
      >
        <div style={{ color: "#95b2c5" }}>
          <h3 style={{ width: "100%", textAlign: "center", color: "#eee" }}>
            Notes
          </h3>
          <h4>New note:</h4>
          <div className="form-field">
            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              width="100%"
              rows={4}
              style={{
                border: "none",
                background: "#021c2d",
                width: "100%",
                color: "#eee",
              }}
            />
          </div>
          <button
            className="primary small"
            onClick={() => {
              handleAddNote();
            }}
          >
            Add note
          </button>
          <br />
          <h4>List:</h4>
          <div style={{ overflowY: "scroll", height: "40vh" }}>
            {selectedCustomer.notes?.map((note) => (
              <div
                style={{ padding: "5px 10px", borderBottom: "1px solid black" }}
              >
                <p>{note.content}</p>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontStyle: "italic", fontSize: "10px" }}>
                    {" "}
                    {note.createdAt}{" "}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DgModal>
    </Widget>
  );
}

export default MyClients;
